import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { apiClient } from 'Shared/api_client';

/**
 * The tag list for one wiki or guide, plus the add/remove calls against the
 * v2 REST endpoints. `queryKey` is owned by the factory that builds this
 * (e.g. `wikiTagsApi`) so it's always derived from the same namespace/title
 * that builds the URL — a caller can't pass a `queryKey` that drifts from the
 * endpoint, which would break the `isMutating(...) === 1` settle logic in
 * `useTags`. The endpoints persist immediately (one tag per call) and return
 * the updated object, so callers reseed the list from `list()`.
 */
export type TagsApi = {
   queryKey: QueryKey;
   list: () => Promise<string[]>;
   add: (tag: string) => Promise<unknown>;
   remove: (tag: string) => Promise<unknown>;
};

/** PUT/DELETE `/wikis/{namespace}/{title}/tag`, GET `/tags?typed`. */
export function wikiTagsApi(namespace: string, title: string): TagsApi {
   const base = `wikis/${encodeURIComponent(namespace)}/${encodeURIComponent(title)}`;
   return {
      queryKey: ['wiki-tags', namespace, title],
      // `?typed` asks for the full typed list ("device:iPhone"): the editor
      // seeds from that list, so reconciling against the default normal-only
      // response would silently drop every typed pill after the first fetch.
      list: async () => {
         const response = await apiClient.get<string[]>(`${base}/tags`, { params: { typed: 1 } });
         return response.data ?? [];
      },
      add: tag => apiClient.put(`${base}/tag`, { tag }),
      // DELETE carries the tag in its body, matching the endpoint's $body['tag'].
      remove: tag => apiClient.delete(`${base}/tag`, { data: { tag } }),
   };
}

/**
 * The tag list as a react-query resource: add/remove update optimistically and
 * reconcile against the server on settle, so the pills stay snappy while the
 * server stays the source of truth. Mirrors the optimistic plumbing in
 * useRepairServices.
 */
export function useTags(api: TagsApi, initialTags: string[]) {
   const queryClient = useQueryClient();
   const { queryKey } = api;

   const { data: tags = [] } = useQuery({
      queryKey,
      queryFn: api.list,
      initialData: initialTags,
      // Only our own mutations change the tag list; the onSettled invalidate
      // below handles freshness, so a window refocus needn't refetch.
      refetchOnWindowFocus: false,
   });

   const optimistic = (apply: (current: string[], tag: string) => string[]) => ({
      onMutate: async (tag: string) => {
         await queryClient.cancelQueries({ queryKey });
         const previous = queryClient.getQueryData<string[]>(queryKey) ?? [];
         queryClient.setQueryData<string[]>(queryKey, apply(previous, tag));
         return { previous };
      },
      onError: (_error: unknown, _tag: string, context?: { previous: string[] }) => {
         if (context) {
            queryClient.setQueryData(queryKey, context.previous);
         }
      },
      // The endpoints return the whole wiki/guide, not just the tags, so
      // refetch the list rather than adopt the response. Only the last
      // settling tag mutation refetches: a mid-flight refetch would overwrite
      // the optimistic entries of the still-pending ones, flickering their
      // pills away until they settle.
      onSettled: () => {
         if (queryClient.isMutating({ mutationKey: queryKey }) === 1) {
            void queryClient.invalidateQueries({ queryKey });
         }
      },
   });

   const addMutation = useMutation({
      mutationKey: queryKey,
      mutationFn: api.add,
      ...optimistic((current, tag) => [...new Set([...current, tag])]),
   });

   const removeMutation = useMutation({
      mutationKey: queryKey,
      mutationFn: api.remove,
      ...optimistic((current, tag) => current.filter(existing => existing !== tag)),
   });

   return {
      tags,
      addTag: addMutation.mutate,
      removeTag: removeMutation.mutate,
      isError: addMutation.isError || removeMutation.isError,
   };
}

/**
 * Resolves once no tag add/remove mutations for `queryKey` are in flight
 * (immediately if none are). The tag endpoints address the page by title, so
 * a page action that renames or navigates away must let these settle first —
 * otherwise an in-flight PUT/DELETE races the rename and lands on the old
 * title (404, or a write to the freshly created redirect).
 */
export function whenTagWritesSettle(queryClient: QueryClient, queryKey: QueryKey): Promise<void> {
   return new Promise(resolve => {
      if (queryClient.isMutating({ mutationKey: queryKey }) === 0) {
         resolve();
         return;
      }
      const unsubscribe = queryClient.getMutationCache().subscribe(() => {
         if (queryClient.isMutating({ mutationKey: queryKey }) > 0) {
            return;
         }
         unsubscribe();
         resolve();
      });
   });
}
